import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import api from "@/api/client";

const CartContext = createContext(null);
const STORAGE_KEY = "talabya_cart_v1";
const RESTAURANT_KEY = "talabya_restaurant_v1";
const DEST_KEY = "talabya_destination_v1";
const PRICING_KEY = "talabya_pricing_v1";

// Delivery pricing DEFAULTS — actual values are fetched live from the backend
// (`/api/settings/delivery-pricing`) on mount and refreshed whenever an admin
// updates them (broadcast over WS as `pricing.updated`).
export const DEFAULT_PRICING = { base_fee: 5, base_km: 5, per_km: 1 };

function haversineKm(a, b) {
  if (!a || !b || a.lat == null || b.lat == null) return null;
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

export function calcDeliveryFee(distanceKm, pricing = DEFAULT_PRICING) {
  const { base_fee, base_km, per_km } = pricing;
  if (distanceKm == null) return base_fee;
  if (distanceKm <= base_km) return base_fee;
  const extra = Math.ceil(distanceKm - base_km);
  return base_fee + extra * per_km;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
    catch { return []; }
  });
  const [fulfillment, setFulfillment] = useState("delivery");
  const [restaurant, setRestaurantState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(RESTAURANT_KEY)) || null; }
    catch { return null; }
  });
  // Destination for delivery — {lat, lng} — used to compute the distance-based fee live
  const [destination, setDestinationState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DEST_KEY)) || null; }
    catch { return null; }
  });

  // Live delivery pricing (admin-configurable). Cached to localStorage so the
  // customer keeps the last-known pricing across reloads while the fresh copy
  // is being fetched from the API.
  const [pricing, setPricing] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(PRICING_KEY));
      if (cached && cached.base_fee != null) return cached;
    } catch { /* ignore */ }
    return DEFAULT_PRICING;
  });
  const wsRef = useRef(null);
  const restaurantRef = useRef(restaurant);
  useEffect(() => { restaurantRef.current = restaurant; }, [restaurant]);

  const applyPricing = useCallback((p) => {
    if (!p || p.base_fee == null) return;
    const clean = {
      base_fee: Number(p.base_fee),
      base_km: Number(p.base_km),
      per_km: Number(p.per_km),
    };
    setPricing(clean);
    try { localStorage.setItem(PRICING_KEY, JSON.stringify(clean)); } catch { /* ignore */ }
  }, []);

  // Fetch fresh pricing on mount + subscribe to WS for live admin updates.
  useEffect(() => {
    let cancelled = false;
    api.get("/settings/delivery-pricing")
      .then(({ data }) => { if (!cancelled) applyPricing(data); })
      .catch(() => { /* keep cached/default */ });

    // Listen to the `restaurants` public topic where the backend emits
    // `pricing.updated` after an admin PATCHes the pricing.
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const wsBase = backend.replace(/^http/, "ws");
      const token = localStorage.getItem("talabya_token") || "";
      const ws = new WebSocket(`${wsBase}/api/ws?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      ws.onopen = () => { try { ws.send(JSON.stringify({ type: "subscribe", topics: ["restaurants"] })); } catch { /* ignore */ } };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (!msg) return;
          if (msg.event === "pricing.updated" && msg.data) applyPricing(msg.data);
          // If the currently-selected cart restaurant has been updated
          // by admin (e.g. delivery pricing changed), refetch it so the
          // cart uses the new values immediately.
          if (msg.event === "restaurant.updated" && msg.data?.id) {
            const cur = restaurantRef.current;
            if (cur && cur.id === msg.data.id) {
              api.get(`/restaurants/${cur.id}`)
                .then(({ data }) => {
                  if (data && data.id) {
                    setRestaurantState(data);
                    try { localStorage.setItem(RESTAURANT_KEY, JSON.stringify(data)); } catch { /* ignore */ }
                  }
                })
                .catch(() => { /* ignore transient errors */ });
            }
          }
        } catch { /* ignore */ }
      };
    } catch { /* WS is optional — HTTP fetch is the source of truth on next mount */ }

    return () => {
      cancelled = true;
      try { wsRef.current?.close(); } catch { /* ignore */ }
    };
  }, [applyPricing]);

  const setRestaurant = useCallback((r) => {
    setRestaurantState(r);
    if (r) localStorage.setItem(RESTAURANT_KEY, JSON.stringify(r));
    else localStorage.removeItem(RESTAURANT_KEY);
  }, []);

  const setDestination = useCallback((d) => {
    setDestinationState(d);
    if (d && d.lat != null && d.lng != null) localStorage.setItem(DEST_KEY, JSON.stringify(d));
    else localStorage.removeItem(DEST_KEY);
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((menuItem, quantity, extras, notes) => {
    const line_total =
      (menuItem.price + extras.reduce((s, e) => s + e.price, 0)) * quantity;
    setItems((prev) => [
      ...prev,
      {
        menu_item_id: menuItem.id,
        name: menuItem.name,
        image: menuItem.image,
        unit_price: menuItem.price,
        quantity,
        extras,
        notes,
        line_total,
      },
    ]);
  }, []);

  const removeAt = (idx) => setItems((p) => p.filter((_, i) => i !== idx));
  const clear = () => setItems([]);

  const updateQty = (idx, qty) =>
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        const nq = Math.max(1, qty);
        const line_total =
          (it.unit_price + it.extras.reduce((s, e) => s + e.price, 0)) * nq;
        return { ...it, quantity: nq, line_total };
      })
    );

  const subtotal = items.reduce((s, i) => s + i.line_total, 0);
  const distance_km = useMemo(() => {
    if (fulfillment !== "delivery" || !restaurant || !destination) return null;
    return haversineKm({lat: restaurant.lat, lng: restaurant.lng}, destination);
  }, [fulfillment, restaurant, destination]);
  // Effective pricing: per-restaurant override wins over the global admin policy.
  const effectivePricing = useMemo(() => {
    const rp = restaurant && restaurant.delivery_pricing;
    if (rp && rp.base_fee != null && rp.base_km != null && rp.per_km != null) {
      return { base_fee: Number(rp.base_fee), base_km: Number(rp.base_km), per_km: Number(rp.per_km) };
    }
    return pricing;
  }, [restaurant, pricing]);
  const delivery_fee = useMemo(() => {
    if (fulfillment !== "delivery" || !items.length) return 0;
    return calcDeliveryFee(distance_km, effectivePricing);
  }, [fulfillment, items.length, distance_km, effectivePricing]);
  const total = subtotal + delivery_fee;
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider
      value={{ items, addItem, removeAt, updateQty, clear,
               subtotal, delivery_fee, distance_km, total, count,
               fulfillment, setFulfillment,
               restaurant, setRestaurant,
               destination, setDestination,
               pricing: effectivePricing }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() { return useContext(CartContext); }
